import { useRef, useEffect, useState, memo } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { Section, CardTitle, CardSubtitle } from '../styles/components';
import { getTypeColor, getStatusColor } from '../utils/colors';
import { formatInvestmentVolume, formatDate } from '../utils/formatters';
import { AnnouncementModal } from './AnnouncementModal';
import type { Announcement, TableSort } from '../types';
import { industryLabel, stateLabel, statusLabel, typeLabel, useT } from '../i18n';

const TableContainer = styled.div`
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 6px; height: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${theme.colors.border}; border-radius: 3px; }

  /* On phones the wide table degraded to a horizontal scroll that hid the key
     columns (Volumen, Status, Standort) off-screen with no affordance. Below
     600px the table reflows into a stacked card list (see Table), so there's
     nothing to scroll sideways and no need for the 500px inner scrollbox —
     let the cards flow into the page scroll instead. */
  @media (max-width: 600px) {
    overflow: visible;
    max-height: none;
  }
`;

// Sans serif throughout the table — overrides the body's serif default.
// `table-layout: fixed` + the <colgroup> below pin every column to a set
// width, so widths stay put when sorting refetches a differently-sized page
// (and when the sort arrow hops between headers). `min-width` keeps the
// columns readable on narrow embeds — TableContainer scrolls horizontally.
const Table = styled.table<{ minWidth?: string }>`
  width: 100%;
  ${p => (p.minWidth ? `min-width: ${p.minWidth};` : '')}
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  font-family: var(--sans-stack);

  /* Phone card layout. The table's many columns can't fit a 390px screen, so
     instead of a sideways-scrolling table (key columns hidden, no affordance)
     each row becomes a self-contained card: the company/industry name is the
     card heading, the project is its subtitle, and every other column renders
     as a labelled "Label … value" line — Volumen included, which used to be
     off-screen. The desktop <colgroup>/min-width are neutralised by display:
     block, and the data-label on each cell (set in the body) feeds the ::before
     label. Tapping a card still opens the detail modal (physical mode). */
  @media (max-width: 600px) {
    display: block;
    min-width: 0;

    thead {
      /* Column headers are redundant once every cell is self-labelling. */
      display: none;
    }
    tbody { display: block; }

    tr {
      /* Containing block for the absolutely-positioned status badge below. */
      position: relative;
      display: block;
      padding: var(--space-3);
      margin-bottom: var(--space-3);
      border: 1px solid var(--color-bg-muted);
      border-radius: var(--radius-m);
      background: var(--color-bg-surface);
    }
    /* The desktop hover-indent makes no sense on a touch card. */
    tr:hover td { transform: none; }

    td {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: var(--space-4);
      padding: 1px 0;
      border-bottom: none;
      text-align: right;
    }
    td::before {
      content: attr(data-label);
      flex-shrink: 0;
      text-align: left;
      font-size: ${scaled(13)};
      font-weight: 500;
      color: var(--color-text-muted);
    }

    /* Project start/end are low-signal on a phone summary — they remain in the
       detail modal (tap the card) and in the desktop table. */
    td.t-hide-mobile { display: none; }

    /* Card heading (company / industry) and subtitle (project): full-width,
       left-aligned, no label prefix. */
    td.t-card-title,
    td.t-card-sub { display: block; text-align: left; }
    td.t-card-title::before,
    td.t-card-sub::before { content: none; }
    td.t-card-title {
      font-size: ${scaled(16)};
      padding-bottom: 1px;
    }
    /* Physical cards float the status badge into the top-right corner, so
       reserve room beside the company name so a long name can't run under it. */
    td.t-card-title--status { padding-right: 96px; }
    td.t-card-sub {
      padding-bottom: var(--space-2);
      color: var(--color-text-secondary);
    }

    /* Status badge pinned top-right, level with the company name. */
    td.t-card-status {
      position: absolute;
      top: var(--space-3);
      right: var(--space-3);
      width: auto;
      padding: 0;
      justify-content: flex-end;
    }
    td.t-card-status::before { content: none; }
  }
`;

// Header row matches the budget title table: Figma "Social S" column labels —
// 13px, regular weight, default black text, no bg, thin separator. Sticky for
// long tables; sortable columns keep the pointer cursor for affordance.
const Th = styled.th<{ sortable?: boolean }>`
  padding: var(--space-3) var(--space-4);
  text-align: left;
  font-size: ${scaled(13)};
  font-weight: 400;
  color: var(--color-text-default);
  border-bottom: 1px solid var(--color-bg-muted);
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: var(--color-bg-surface);
  z-index: 1;
  cursor: ${p => p.sortable ? 'pointer' : 'default'};
  user-select: none;
`;

// Body cells: generous vertical padding, hairline separator. Cell text
// is bumped to 14px — a step up from the column-header size (13px) so the
// data rows read as the primary content. Selection
// tints the cell; the transform transition drives the row-hover indent.
const Td = styled.td<{ isSelected?: boolean }>`
  padding: var(--space-4) var(--space-4);
  font-size: ${scaled(14)};
  line-height: 1.3;
  /* German compounds are longer than these columns: "Halbleiterfertigungs-
     stätte" is one unbreakable word that overhangs the 156px project column by
     6-11px at the design type size (the cell's overflow is visible, so it runs
     under its neighbour). Let the browser break inside the word when it has to;
     normal words are unaffected. */
  overflow-wrap: anywhere;
  color: var(--color-text-default);
  border-bottom: 1px solid var(--color-bg-muted);
  background: ${props => props.isSelected ? 'color-mix(in srgb, var(--primary-color) 15%, transparent)' : 'transparent'};
  transition: transform 0.3s ease-in-out;
`;

// Matches the budget title table: hover does NOT change the background — the
// only hover affordance is a small rightward indent of the row, eased over
// 0.3s (transition lives on Td). Selection still tints the cells.
const Tr = styled.tr<{ clickable?: boolean }>`
  cursor: ${p => p.clickable !== false ? 'pointer' : 'default'};
  &:hover td { transform: ${p => p.clickable !== false ? 'translateX(8px)' : 'none'}; }
`;

const TypeDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background-color: ${props => props.color};
  margin-right: ${theme.spacing.sm};
  flex-shrink: 0;
`;

// Soft pastel pill matching the website screenshot (announced=grey,
// in progress=amber, cancelled=pink, finished=green). Mix the source
// status color heavily with white for a faded bg, keep text dark.
const StatusBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.3em 0.8em;
  border-radius: 999px;
  font-family: var(--sans-stack);
  font-size: ${scaled(12)};
  font-weight: ${theme.fontWeight.normal};
  background: color-mix(in srgb, ${props => props.color} 35%, #ffffff);
  color: var(--color-text-default);
  white-space: nowrap;
`;

const TypeCell = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const ValueCell = styled.span`
  font-weight: ${theme.fontWeight.semibold};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const LocationsList = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.4;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  /* On narrow embeds the right side (toggle / search / export) drops onto
     its own row below the title instead of overflowing the page. */
  flex-wrap: wrap;
  row-gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 auto;
  flex-wrap: wrap;
  min-width: 0;
  gap: ${theme.spacing.sm};
`;

// Same affordance as the budget title table's export trigger: bordered
// square icon button that opens the CSV export dialog.
const ExportButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 18px;
  /* Clip the icon to the button box so a not-yet-loaded icon font (which
     falls back to the ligature text, e.g. "download") can't briefly overflow
     and widen the row. */
  overflow: hidden;
  border: 1px solid ${theme.colors.border};
  background: transparent;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.05);
    border-color: ${theme.colors.text.secondary};
    color: ${theme.colors.text.primary};
  }
`;

const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  margin-top: ${theme.spacing.sm};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};

  /* On phones the full page-button row + range label won't fit side by side,
     which squeezes "1–50 von 283" into a vertical stack. Drop to a column so
     the range sits on its own line above the (wrapping, centered) buttons. */
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing.sm};
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};

  @media (max-width: 600px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

// Shared `.button.button--outline.button--size-s`. The active modifier on
// outline-style buttons is `.button--outline--active` (single concatenated
// class), NOT `.button--outline.button--active` — the shared SCSS
// authoring varies between variants and outline uses the concatenated
// form. Get this wrong and the active page paints identical to inactive.
interface PageButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}
const PageButtonBase = styled.button`
  &:disabled { opacity: 0.4; cursor: default; }
`;
const PageButton = ({ active, className, ...rest }: PageButtonProps) => (
  <PageButtonBase
    {...rest}
    className={`button button--outline${active ? ' button--outline--active' : ''} button--size-s ${className ?? ''}`.trim()}
  />
);

const SortIndicator = styled.span`
  margin-left: 4px;
  font-size: 10px;
`;

const AggregateFilterBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  background: ${theme.colors.text.primary}0a;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

const ClearFilterButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${theme.fontSize.md};
  color: ${theme.colors.text.secondary};
  padding: 0 4px;
  line-height: 1;
  font-family: ${theme.fontFamily};

  &:hover {
    color: ${theme.colors.text.primary};
  }
`;

interface InvestmentTableProps {
  mode: 'physical' | 'financial';
  rows: Announcement[];
  totalCount: number;
  page: number;
  pageSize: number;
  sort: TableSort;
  selectedClusterId: number | null;
  aggregateFilterActive?: boolean;
  aggregateFilterLabel?: string;
  onClearAggregateFilter?: () => void;
  onRowClick: (clusterId: number) => void;
  onModalClose?: () => void;
  onPageChange: (page: number) => void;
  onSortChange: (sort: TableSort) => void;
  headerRight?: React.ReactNode;
  /** When set, shows the CSV-export icon button in the header. */
  onExport?: () => void;
}

export const InvestmentTable = memo(function InvestmentTable({
  mode,
  rows,
  totalCount,
  page,
  pageSize,
  sort,
  selectedClusterId,
  aggregateFilterActive,
  aggregateFilterLabel,
  onClearAggregateFilter,
  onRowClick,
  onModalClose,
  onPageChange,
  onSortChange,
  headerRight,
  onExport,
}: InvestmentTableProps) {
  const m = useT();
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const [modalAnnouncement, setModalAnnouncement] = useState<Announcement | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Scroll to selected row after render
  useEffect(() => {
    if (selectedClusterId == null) return;
    const timer = setTimeout(() => {
      const row = rowRefs.current.get(selectedClusterId);
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [selectedClusterId, page]);

  const handleSort = (field: string) => {
    if (sort.field === field) {
      onSortChange({ field, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      onSortChange({ field, dir: 'desc' });
    }
  };

  const renderSortIndicator = (field: string) => {
    if (sort.field !== field) return null;
    return <SortIndicator>{sort.dir === 'asc' ? '\u25B2' : '\u25BC'}</SortIndicator>;
  };

  const renderPageButtons = () => {
    const buttons: React.ReactNode[] = [];
    const maxVisible = 7;

    let start = Math.max(0, page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible);
    if (end - start < maxVisible) start = Math.max(0, end - maxVisible);

    if (start > 0) {
      buttons.push(<PageButton key={0} onClick={() => onPageChange(0)}>1</PageButton>);
      if (start > 1) buttons.push(<span key="e1" style={{ padding: '0 4px' }}>...</span>);
    }

    for (let i = start; i < end; i++) {
      buttons.push(
        <PageButton key={i} active={i === page} onClick={() => onPageChange(i)}>
          {i + 1}
        </PageButton>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) buttons.push(<span key="e2" style={{ padding: '0 4px' }}>...</span>);
      buttons.push(<PageButton key={totalPages - 1} onClick={() => onPageChange(totalPages - 1)}>{totalPages}</PageButton>);
    }

    return buttons;
  };

  return (
    <Section>
      <HeaderRow>
        <div>
          <CardTitle>{mode === 'physical' ? m.tiers.physical : m.tiers.financial}</CardTitle>
          <CardSubtitle>{m.table.entries(totalCount)}</CardSubtitle>
        </div>
        <HeaderRight>
          {headerRight}
          {onExport && (
            <ExportButton onClick={onExport} title={m.table.exportCsv}>
              <span className="icon" data-icon="download" />
            </ExportButton>
          )}
        </HeaderRight>
      </HeaderRow>

      {aggregateFilterActive && (
        <AggregateFilterBanner>
          <span>{aggregateFilterLabel ? `${aggregateFilterLabel} —` : m.table.mapSelection} {m.table.investments(totalCount)}</span>
          <ClearFilterButton onClick={onClearAggregateFilter} title={m.table.clearFilter}>
            ×
          </ClearFilterButton>
        </AggregateFilterBanner>
      )}

      <TableContainer>
        <Table minWidth={mode === 'physical' ? '1530px' : '1060px'}>
          {/* Column widths must match the conditional columns below 1:1, in order. */}
          <colgroup>
            {/* Unternehmen (physical) / Industrie (financial) */}
            <col style={{ width: mode === 'physical' ? '160px' : '240px' }} />
            {mode === 'physical' && <col style={{ width: '180px' }} />}{/* Projekt */}
            {mode === 'physical' && <col style={{ width: '200px' }} />}{/* Industrie */}
            <col style={{ width: mode === 'physical' ? '150px' : '180px' }} />{/* Investitionsart */}
            <col style={{ width: mode === 'physical' ? '110px' : '140px' }} />{/* Volumen */}
            <col style={{ width: mode === 'physical' ? '110px' : '140px' }} />{/* Erste Meldung */}
            <col style={{ width: mode === 'physical' ? '110px' : '140px' }} />{/* Letztes Update */}
            {mode === 'physical' && <col style={{ width: '110px' }} />}{/* Projektstart */}
            {mode === 'physical' && <col style={{ width: '110px' }} />}{/* Projektende */}
            <col style={{ width: mode === 'physical' ? '160px' : '220px' }} />{/* Standort */}
            {mode === 'physical' && <col style={{ width: '130px' }} />}{/* Status */}
          </colgroup>
          <thead>
            <tr>
              {mode === 'physical' ? (
                <Th sortable onClick={() => handleSort('company')}>
                  {m.table.company}{renderSortIndicator('company')}
                </Th>
              ) : (
                <Th sortable onClick={() => handleSort('industry')}>
                  {m.table.industry}{renderSortIndicator('industry')}
                </Th>
              )}
              {mode === 'physical' && (
                <Th sortable onClick={() => handleSort('project')}>
                  {m.table.project}{renderSortIndicator('project')}
                </Th>
              )}
              {mode === 'physical' && (
                <Th sortable onClick={() => handleSort('industry')}>
                  {m.table.industry}{renderSortIndicator('industry')}
                </Th>
              )}
              <Th sortable onClick={() => handleSort('type')}>
                {m.table.type}{renderSortIndicator('type')}
              </Th>
              <Th sortable style={{ textAlign: 'right' }} onClick={() => handleSort('volume')}>
                {m.table.volume}{renderSortIndicator('volume')}
              </Th>
              <Th sortable onClick={() => handleSort('firstDate')}>
                {m.table.firstDate}{renderSortIndicator('firstDate')}
              </Th>
              <Th sortable onClick={() => handleSort('date')}>
                {m.table.lastUpdate}{renderSortIndicator('date')}
              </Th>
              {mode === 'physical' && (
                <Th sortable onClick={() => handleSort('projectStart')}>
                  {m.table.projectStart}{renderSortIndicator('projectStart')}
                </Th>
              )}
              {mode === 'physical' && (
                <Th sortable onClick={() => handleSort('projectEnd')}>
                  {m.table.projectEnd}{renderSortIndicator('projectEnd')}
                </Th>
              )}
              <Th>{m.table.location}</Th>
              {mode === 'physical' && (
                <Th sortable onClick={() => handleSort('status')}>
                  {m.table.status}{renderSortIndicator('status')}
                </Th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(a => {
              const isSelected = a.cluster_id === selectedClusterId;
              return (
                <Tr
                  key={a.id}
                  clickable={mode === 'physical'}
                  ref={el => {
                    if (el && a.cluster_id != null) rowRefs.current.set(a.cluster_id, el);
                  }}
                  onClick={mode === 'physical' ? () => {
                    if (a.cluster_id != null) onRowClick(a.cluster_id);
                    setModalAnnouncement(a);
                  } : undefined}
                >
                  {mode === 'physical' ? (
                    <Td className="t-card-title t-card-title--status" isSelected={isSelected}>
                      <strong>{a.investing_company || '-'}</strong>
                    </Td>
                  ) : (
                    <Td className="t-card-title" isSelected={isSelected}>
                      <strong>{a.primary_industry ? industryLabel(a.primary_industry) : '-'}</strong>
                    </Td>
                  )}
                  {mode === 'physical' && (
                    <Td className="t-card-sub" isSelected={isSelected}>
                      {a.project_name || '-'}
                    </Td>
                  )}
                  {mode === 'physical' && (
                    <Td data-label={m.table.industry} isSelected={isSelected}>
                      {a.primary_industry ? industryLabel(a.primary_industry) : '-'}
                    </Td>
                  )}
                  <Td data-label={m.table.typeShort} isSelected={isSelected}>
                    <TypeCell>
                      <TypeDot color={getTypeColor(a.type_of_investment)} />
                      <span>{a.type_of_investment ? typeLabel(a.type_of_investment) : '-'}</span>
                    </TypeCell>
                  </Td>
                  <Td data-label={m.table.volume} isSelected={isSelected} style={{ textAlign: 'right' }}>
                    <ValueCell>
                      {formatInvestmentVolume(a.investment_volume_value_eur)}
                    </ValueCell>
                  </Td>
                  <Td data-label={m.table.firstDate} isSelected={isSelected}>
                    {formatDate(a.first_announced_date)}
                  </Td>
                  <Td data-label={m.table.lastUpdate} isSelected={isSelected}>
                    {formatDate(a.announced_event_date)}
                  </Td>
                  {mode === 'physical' && (
                    <Td className="t-hide-mobile" data-label={m.table.projectStart} isSelected={isSelected}>
                      {formatDate(a.project_start_date)}
                    </Td>
                  )}
                  {mode === 'physical' && (
                    <Td className="t-hide-mobile" data-label={m.table.projectEnd} isSelected={isSelected}>
                      {formatDate(a.project_end_date)}
                    </Td>
                  )}
                  <Td data-label={m.table.location} isSelected={isSelected}>
                    <LocationsList>
                      {a.locations.length > 0
                        ? a.locations.map(l => `${l.city_name}${l.federal_state ? ` (${stateLabel(l.federal_state)})` : ''}`).join(', ')
                        : '-'
                      }
                    </LocationsList>
                  </Td>
                  {mode === 'physical' && (
                    <Td className="t-card-status" data-label={m.table.status} isSelected={isSelected}>
                      <StatusBadge color={getStatusColor(a.investment_status)}>
                        {a.investment_status ? statusLabel(a.investment_status) : '-'}
                      </StatusBadge>
                    </Td>
                  )}
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <PaginationBar>
          <span>
            {m.table.range(page * pageSize + 1, Math.min((page + 1) * pageSize, totalCount), totalCount)}
          </span>
          <PaginationButtons>
            <PageButton disabled={page === 0} onClick={() => onPageChange(page - 1)}>
              {m.table.previous}
            </PageButton>
            {renderPageButtons()}
            <PageButton disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>
              {m.table.next}
            </PageButton>
          </PaginationButtons>
        </PaginationBar>
      )}

      {modalAnnouncement && (
        <AnnouncementModal
          announcement={modalAnnouncement}
          onClose={() => { setModalAnnouncement(null); onModalClose?.(); }}
        />
      )}
    </Section>
  );
});
